import { useRef, useState, useCallback } from "react";

interface LongPressButtonProps {
	onConfirm?: () => void;
	duration?: number;
	className?: string;
	label?: string;
	labelHolding?: string;
	fillClassName?: string;
}

/**
 * A button that triggers `onConfirm` after being held for `duration` ms.
 * Uses a Bootstrap `.btn` with a filling overlay to indicate progress.
 */
function LongPressButton({
	onConfirm,
	duration = 2000,
	className = "btn btn-primary",
	label = "Hold 2s to Confirm",
	labelHolding = "Keep holding…",
	fillClassName = "bg-white bg-opacity-25",
}: LongPressButtonProps) {
	const [progress, setProgress] = useState(0);
	const [holding, setHolding] = useState(false);
	const rafRef = useRef<number | null>(null);
	const startRef = useRef<number | null>(null);

	const cleanup = useCallback(() => {
		if (rafRef.current) cancelAnimationFrame(rafRef.current);
		rafRef.current = null;
		startRef.current = null;
		setHolding(false);
		setProgress(0);
	}, []);

	const tick = useCallback(
		(t: number) => {
			if (startRef.current == null) startRef.current = t;
			const elapsed = t - startRef.current;
			const pct = Math.min(elapsed / duration, 1);
			setProgress(pct);
			if (pct < 1) {
				rafRef.current = requestAnimationFrame(tick);
			} else {
				cleanup();
				onConfirm?.();
			}
		},
		[cleanup, duration, onConfirm],
	);

	const begin = useCallback(
		(e: React.PointerEvent | React.KeyboardEvent) => {
			e.preventDefault?.();
			if (rafRef.current) return;
			setHolding(true);
			startRef.current = null;
			rafRef.current = requestAnimationFrame(tick);
		},
		[tick],
	);

	const cancel = useCallback(() => {
		cleanup();
	}, [cleanup]);

	const onKeyDown = useCallback(
		(e: React.KeyboardEvent) => {
			if (e.code === "Space" || e.code === "Enter") begin(e);
		},
		[begin],
	);

	return (
		<button
			type="button"
			className={`${className} position-relative overflow-hidden`}
			style={{ touchAction: "manipulation" }}
			aria-label={holding ? "Keep holding to confirm" : "Hold to confirm"}
			aria-pressed={holding}
			aria-valuemin={0}
			aria-valuemax={100}
			aria-valuenow={Math.round(progress * 100)}
			onPointerDown={begin}
			onPointerUp={cancel}
			onPointerLeave={cancel}
			onPointerCancel={cancel}
			onKeyDown={onKeyDown}
			onKeyUp={cancel}
		>
			<span className="position-relative">{holding ? labelHolding : label}</span>
			<span
				className={`position-absolute top-0 start-0 h-100 ${fillClassName}`}
				style={{
					width: `${progress * 100}%`,
					pointerEvents: "none",
					transition: holding ? "none" : "width .15s ease",
				}}
				aria-hidden="true"
			/>
		</button>
	);
}

export default LongPressButton;
